import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

/**
 * CalendarItemManager - Manage calendar event visibility and pinning
 * Allows hiding/showing synced events and adding custom manual events
 */
export default function CalendarItemManager({ events, config, onConfigUpdate }) {
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    start_time: '',
    location: '',
    description: ''
  });

  const handleToggleVisibility = async (eventId, currentVisible) => {
    try {
      const { error } = await supabase
        .from('calendar_events')
        .update({ portal_visible: !currentVisible })
        .eq('id', eventId);

      if (error) throw error;
      
      toast.success(currentVisible ? 'Event hidden from portal' : 'Event visible on portal');
      
      // Force reload
      onConfigUpdate({ ...config, _reload: Date.now() });
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event');
    }
  };

  const handleTogglePin = async (eventId, currentPinned) => {
    try {
      const { error } = await supabase
        .from('calendar_events')
        .update({ portal_pinned: !currentPinned })
        .eq('id', eventId);

      if (error) throw error;
      
      toast.success(currentPinned ? 'Event unpinned' : 'Event pinned to top');
      
      // Force reload
      onConfigUpdate({ ...config, _reload: Date.now() });
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event');
    }
  };

  const handleAddCustomEvent = async () => {
    if (!newEvent.title || !newEvent.start_time) {
      toast.error('Title and date/time are required');
      return;
    }

    try {
      // Get team_id from first event (all events belong to same team)
      const teamId = events[0]?.team_id;
      if (!teamId) {
        toast.error('Unable to determine team');
        return;
      }

      const { error } = await supabase
        .from('calendar_events')
        .insert({
          team_id: teamId,
          title: newEvent.title,
          start_time: newEvent.start_time,
          location: newEvent.location || null,
          description: newEvent.description || null,
          event_type: 'other',
          source: 'manual',
          portal_visible: true,
          portal_pinned: false
        });

      if (error) throw error;
      
      toast.success('Custom event added');
      setShowAddEvent(false);
      setNewEvent({ title: '', start_time: '', location: '', description: '' });
      
      // Force reload
      onConfigUpdate({ ...config, _reload: Date.now() });
    } catch (error) {
      console.error('Error adding event:', error);
      toast.error('Failed to add event');
    }
  };

  return (
    <div className="space-y-4">
      {/* Add Custom Event */}
      <div className="bg-white rounded-lg p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-900">Custom Events</h4>
          <button
            onClick={() => setShowAddEvent(!showAddEvent)}
            className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {showAddEvent ? 'Cancel' : '+ Add Event'}
          </button>
        </div>

        {showAddEvent && (
          <div className="space-y-2 mt-3">
            <input
              type="text"
              value={newEvent.title}
              onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
              placeholder="Event title"
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <input
              type="datetime-local"
              value={newEvent.start_time}
              onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <input
              type="text"
              value={newEvent.location}
              onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
              placeholder="Location (optional)"
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <textarea
              value={newEvent.description}
              onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
              placeholder="Description (optional)"
              rows={2}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <button
              onClick={handleAddCustomEvent}
              className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Add Event
            </button>
          </div>
        )}
      </div>

      {/* Event List */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-gray-900">All Events</h4>
        
        {events.length === 0 && (
          <p className="text-sm text-gray-500">No events to manage</p>
        )}

        {events.map((event) => (
          <div 
            key={event.id}
            className="bg-white rounded-lg p-3 border border-gray-200"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {event.title}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(event.start_time).toLocaleString()}
                </p>
                {event.location && (
                  <p className="text-xs text-gray-500 mt-1">📍 {event.location}</p>
                )}
                {event.source === 'manual' && (
                  <span className="inline-block text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded mt-1">
                    Custom
                  </span>
                )}
              </div>
              
              {event.pinned && (
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded ml-2">
                  Pinned
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleToggleVisibility(event.id, true)}
                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
              >
                👁️ Hide
              </button>
              <button
                onClick={() => handleTogglePin(event.id, event.pinned)}
                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
              >
                📌 {event.pinned ? 'Unpin' : 'Pin'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Help Text */}
      <p className="text-xs text-gray-500">
        Tip: Hide past events or less important events. Pin key upcoming events to keep them visible.
      </p>
    </div>
  );
}

